import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Copy, CheckCircle2 } from 'lucide-react';
import { copyPageVariables } from '@/services/api/broadcast';
import type { Page } from '@/types/broadcast';
import type { PageVariablesValidation } from '@/types/broadcast';

interface VariablesCopyDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    pages: PageVariablesValidation[];
    selectedPages: Page[];
    onFixed: () => void;
}

// overrides por página: pageId -> varName -> value
type OverridesPerPage = Record<number, Record<string, string>>;

export default function VariablesCopyDialog({
    open,
    onOpenChange,
    pages,
    selectedPages,
    onFixed,
}: VariablesCopyDialogProps) {
    const pagesWithMissing = pages.filter(p => !p.can_resolve);
    const pagesOk = pages.filter(p => p.can_resolve);

    const [sourcePageId, setSourcePageId] = useState<string>(() =>
        pagesOk.length > 0 ? String(pagesOk[0].id) : (selectedPages[0] ? String(selectedPages[0].id) : '')
    );

    // Inicializa overrides por página: cada página tem seu próprio conjunto de valores
    const [overridesPerPage, setOverridesPerPage] = useState<OverridesPerPage>(() => {
        const initial: OverridesPerPage = {};
        for (const p of pagesWithMissing) {
            initial[p.id] = Object.fromEntries(
                p.variables_missing.map(v => [v, ''])
            );
        }
        return initial;
    });

    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCopy = async () => {
        if (!sourcePageId) return;
        setLoading(true);
        setError(null);
        try {
            // target_page_ids[i] recebe overrides_per_page[i]
            const target_page_ids = pagesWithMissing.map(p => p.id);
            const overrides_per_page = pagesWithMissing.map(p => {
                const overrides = overridesPerPage[p.id] ?? {};
                return Object.fromEntries(
                    Object.entries(overrides).filter(([, v]) => v.trim() !== '')
                );
            });
            await copyPageVariables({
                source_page_id: Number(sourcePageId),
                target_page_ids,
                overrides_per_page,
            });
            setDone(true);
            setTimeout(() => {
                onFixed();
                onOpenChange(false);
            }, 800);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Erro ao copiar variáveis');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Copy className="h-5 w-5 text-blue-600" />
                        Corrigir variáveis faltantes
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 min-h-0 overflow-y-auto space-y-5 py-2 pr-1">
                    {/* Pages with issues */}
                    <div>
                        <p className="text-sm text-gray-600 mb-2">
                            {pagesWithMissing.length === 1
                                ? 'A página abaixo está com variáveis faltando:'
                                : `As ${pagesWithMissing.length} páginas abaixo estão com variáveis faltando:`}
                        </p>
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                            {pagesWithMissing.map(p => (
                                <div key={p.id} className="flex items-center justify-between gap-3 rounded-md bg-red-50 px-3 py-2 text-sm">
                                    <span className="font-medium text-gray-800 truncate">{p.page_name}</span>
                                    <div className="flex gap-1 flex-wrap justify-end">
                                        {p.variables_missing.map(v => (
                                            <Badge key={v} variant="destructive" className="text-xs">{v}</Badge>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Source page */}
                    <div className="space-y-1">
                        <Label>Copiar variáveis de</Label>
                        <Select value={sourcePageId} onValueChange={setSourcePageId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Selecione a página de origem" />
                            </SelectTrigger>
                            <SelectContent>
                                {selectedPages.map(p => (
                                    <SelectItem key={p.id} value={String(p.id)}>
                                        {p.page_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                            As variáveis desta página serão copiadas para as páginas com problema.
                        </p>
                    </div>

                    {/* Overrides por página - cada página tem seus próprios valores */}
                    {pagesWithMissing.length > 0 && (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                <p className="text-sm font-medium text-gray-700">
                                    Definir valores para variáveis ausentes na origem (opcional)
                                </p>
                            </div>
                            <p className="text-xs text-gray-500">
                                Cada página pode ter valores diferentes para as mesmas variáveis.
                            </p>
                            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
                                {pagesWithMissing.map(page => (
                                    <div key={page.id} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                                        <p className="text-sm font-medium text-gray-800">{page.page_name}</p>
                                        {page.variables_missing.map(varName => (
                                            <div key={`${page.id}-${varName}`} className="space-y-1">
                                                <Label className="text-xs font-mono text-gray-600">{`{{${varName}}}`}</Label>
                                                <Input
                                                    value={overridesPerPage[page.id]?.[varName] ?? ''}
                                                    onChange={e => setOverridesPerPage(prev => ({
                                                        ...prev,
                                                        [page.id]: {
                                                            ...(prev[page.id] ?? {}),
                                                            [varName]: e.target.value,
                                                        },
                                                    }))}
                                                    placeholder={`Valor para ${varName}`}
                                                    className="text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-red-600">{error}</p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleCopy}
                        disabled={loading || done || !sourcePageId}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {done
                            ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Copiado!</>
                            : loading
                                ? 'Copiando...'
                                : <><Copy className="h-4 w-4 mr-1" /> Copiar variáveis</>
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
